import { useForm } from '@kanda-libs/ks-component-ts';
import type { StringIndexedObject } from '@kanda-libs/ks-design-library';
import type { Job } from '@kanda-libs/ks-frontend-services';
import useCreateJobContextValue from 'pages/CreateJob/CreateJob-useCreateJobContextValue';
import useIsExampleJob from 'pages/CreateJob/CreateJob-useIsExampleJob';
import { useCallback, useMemo } from 'react';
import { getSelectedFinanceRates } from 'utils';

import useSubmitChanges from 'pages/CreateJob/CreateJob-useSubmitChanges';
import { checkJobChangesRequested } from 'utils/Job-functions';
import useIsSubscribed from '../../../../../../hooks/useIsSubscribed';
import useSubmitDraft from '../../../../CreateJob-useSubmitDraft';
import useSubmitJob from '../../../../CreateJob-useSubmitJob';

import { BASE_BUTTON_PROPS } from './Summary-constants';

export interface SummaryContainerFormValues {
  job: Partial<Job>;
}

export interface SummaryContainerChildrenArgs {
  prevStep: () => void;
  isSubmitting: boolean;
  showSubmit: boolean;
  nextButtonProps: {
    disabled: boolean;
    icon: string;
    label: string;
    left: boolean;
    submit: boolean;
  };
  form: ReturnType<typeof useForm<SummaryContainerFormValues>>;
  handleSubmit: (formValues: StringIndexedObject) => void;
}

export interface SummaryContainerProps {
  children: (args: SummaryContainerChildrenArgs) => JSX.Element;
}

const SummaryContainer = ({ children }: SummaryContainerProps) => {
  const { data, prevStep } = useCreateJobContextValue();

  const company = data?.company;

  const { handleSubmit: submitJob, isSubmitting: jobIsSubmitting } =
    useSubmitJob();

  const { handleSubmit: submitChanges, isSubmitting: changesIsSubmitting } =
    useSubmitChanges();

  const { handleSubmit: submitDraft } = useSubmitDraft();

  const isSubmitting = useMemo(
    () => jobIsSubmitting || changesIsSubmitting,
    [jobIsSubmitting, changesIsSubmitting],
  );

  const form = useForm<SummaryContainerFormValues>({
    mode: 'onBlur',
    defaultValues: {
      ...data,
      job: {
        ...data?.job,
        notes: [''],
      },
    },
  });

  const changesRequested = useMemo(() => {
    const { job } = data;
    if (!job) return false;
    return checkJobChangesRequested(job);
  }, [data]);

  const handler = useMemo(() => {
    const id = data?.job?.id;
    if (!id) return submitJob;
    if (changesRequested) return submitChanges;
    return submitDraft;
  }, [data, changesRequested, submitJob, submitChanges, submitDraft]);

  const handleSubmit = useCallback(
    (formValues: StringIndexedObject) => {
      const notes = formValues?.job?.notes || null;
      const selectedRates = formValues?.job?.finance_options || undefined;
      const formData = {
        ...data,
        job: {
          ...(data.job as Job),
          finance_options: getSelectedFinanceRates(
            data.company?.finance_rates || [],
            selectedRates,
          ),
          ...(notes ? { notes } : {}),
        },
      };
      handler(formData);
    },
    [data, handler],
  );

  const { isSubscribed } = useIsSubscribed();

  const isExampleJob = useIsExampleJob();

  const showSubmit =
    isExampleJob || (company?.lifecycle === 'verified' && isSubscribed);

  return children({
    prevStep,
    isSubmitting,
    showSubmit,
    nextButtonProps: {
      ...BASE_BUTTON_PROPS,
      disabled: isSubmitting || !showSubmit,
    },
    form,
    handleSubmit,
  });
};

SummaryContainer.displayName = 'CreateJob-Summary-Desktop-container';

export default SummaryContainer;
